import { Visibility, VisibilityOutlined } from "@mui/icons-material";
import { Button, CircularProgress, InputLabel, TextField } from "@mui/material";
import { useClickArea } from "hooks";
import React, { useState, useRef } from "react";
import { useFormik } from "formik";

import { Auth } from "./Signup";
import * as Yup from "yup";
import Swal from "sweetalert2";
import {
  RecaptchaVerifier,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
} from "firebase/auth";
import { auth } from "configs/firebaseConfigs";
import { useNavigate, useSearchParams } from "react-router-dom";
import PhoneInput from "react-phone-number-input";
import useDataFetch from "hooks/useDataFetch";

const Login = () => {
  const [visiblePassword, setVisiblePassword] = useState(false);
  const [phoneValue, setPhoneValue] = useState("");
  const [verifyByOtp, setVerifyByOtp] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [confirmResult, setConfirmResult] = useState(null);
  const [loadingBtn, setLoadingBtn] = useState(false);
  const [loadingOtp, setLoadingOtp] = useState(false);
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [otpGenerated, setOtpGenerated] = useState(false);

  const allUser = useDataFetch("/users");
  const navigate = useNavigate();

  const otpVerifyRef = useRef(null);
  useClickArea(otpVerifyRef, () => {
    setVerifyByOtp(false);
  });

  const [urlParams] = useSearchParams();

  const redirectUrl = urlParams.get("redirect");

  // console.log("redirectUrl", redirectUrl);

  const phoneSignIn = useFormik({
    initialValues: {
      phone: "",
      otp: "",
    },
    validationSchema: Yup.object({
      phone: Yup.string()
        .required("Phone number is required")
        .min(10, "At least 10 digits")
        .matches(
          /^[+]*[(]{0,1}[0-9]{1,3}[)]{0,1}[-\s./0-9]*$/g,
          "Phone number is invalid"
        ),
      otp: Yup.string().required("OTP is required"),
    }),
    onSubmit: async (values) => {
      try {
        setLoadingBtn(true);

        // console.log(values);
        await confirmResult?.confirm(values?.otp); // User signed in successfully.
        setLoadingBtn(false);
        navigate("/profile");
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Wrong OTP",
          text: "Please try again",
        });
        setLoadingBtn(false);
      }
    },
  });

  // console.log(loadingBtn);

  const generateOtp = () => {
    // console.log("otp");
    // console.log(phoneValue);
    try {
      if (otpGenerated) {
        Swal.fire({
          icon: "error",
          title: "OTP already generated",
          text: "Please refresh try again for new OTP",
        });
        return;
      }
      setLoadingOtp(true);

      let userExist = allUser?.data?.some(
        (user) => user?.phoneNumber === phoneValue
      );
      if (!userExist) {
        Swal.fire({
          icon: "error",
          title: "User not found!",
          text: "Please signup first",
        });
        setLoadingOtp(false);
        return;
      }

      setOtpGenerated(true);

      window.reCaptchaVerifier = new RecaptchaVerifier(
        "re-captcha-verifier2",
        {
          size: "invisible",
          callback: (response) => {},
        },
        auth
      );
      let phoneNumber = phoneValue;
      signInWithPhoneNumber(auth, phoneNumber, window.reCaptchaVerifier)
        .then((confirmationResult) => {
          Swal.fire({
            title: "Message sent",
            text: "Verify your OTP  ",
            icon: "success",
            showConfirmButton: false,
            timer: 1500,
          });
          setConfirmResult(confirmationResult);
          setShowOTP(true);
          setLoadingOtp(false);
        })
        .catch((error) => {
          setLoadingOtp(false);
          Swal.fire({
            title: "OOPs! Something went wrong ",
            text: "",
            icon: "error",
            confirmButtonText: "Try Again",
          });
          // console.log(error);
        });
    } catch (error) {
      // console.log(error);
      setLoadingOtp(false);
      Swal.fire({
        title: "OOPs! Something went wrong ",
        text: "",
        icon: "error",
        confirmButtonText: "Try Again",
      });
    }
  };

  const emailSignIn = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().email("Invalid email").required("Required"),
      password: Yup.string().required("Required"),
    }),
    onSubmit: (values) => {
      try {
        setLoadingLogin(true);
        // console.log(values);
        let userExist = allUser?.data?.some((user) => {
          return user?.email === values.email;
        });
        if (!userExist) {
          Swal.fire({
            title: "User Doesn't Exist",
            text: "Please Signup",
            icon: "error",
          });
          return;
        }
        signInWithEmailAndPassword(auth, values?.email, values?.password)
          .then((userCredential) => {
            // Signed in
            setLoadingLogin(false);
            navigate(redirectUrl || "/profile");
            // ...
          })
          .catch((error) => {
            setLoadingLogin(false);
            Swal.fire({
              title: "Error",
              text: "Email or Password is incorrect",
              icon: "error",
            });
            // console.log(error);
          });
      } catch (error) {
        // console.log(error);
        setLoadingLogin(false);
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Something went wrong!",
        });
      }
    },
  });

  return (
    <Auth
      header={{
        tagline: "Don't have a account  ",
        btn: "Sign up",
        title: "Welcome Back",
        titleTag: "Sign in to your account",
        actionBtn: "Login",
        socialTag: "Login with",
      }}
      link={"/signup"}
    >
      {verifyByOtp && (
        <div
          style={{
            userSelect: "none",
          }}
          className="login-by-otp flex-row align-items-center justify-content-center"
        >
          <div className="login-by-otp-inner" ref={otpVerifyRef}>
            <form onSubmit={phoneSignIn.handleSubmit}>
              <div className="sign-up-phone auth-input-form w-100">
                <InputLabel> Phone </InputLabel>

                <PhoneInput
                  international
                  countryCallingCodeEditable={false}
                  className="w-100 country-phone-input flex-row align-items-center "
                  placeholder="Enter phone number"
                  value={phoneValue}
                  disabled={showOTP}
                  onChange={(value) => {
                    setPhoneValue(value);
                    phoneSignIn.setFieldValue("phone", value);
                  }}
                  onBlur={(value) => {
                    phoneSignIn.handleBlur("phone");
                  }}
                  defaultCountry="IN"
                />
                <h1
                  style={{
                    width: "100%",
                    fontSize: "12px",
                    color: "#d32f2f",
                    paddingTop: "0.5rem",
                    fontWeight: "400",
                  }}
                >
                  {phoneSignIn.touched.phone && phoneSignIn.errors.phone}
                </h1>

                <span className="sign-up-otp">
                  <Button type="button" onClick={generateOtp}>
                    {loadingOtp ? (
                      <CircularProgress
                        sx={{
                          color: "white",
                          width: "2rem !important",
                          height: "2rem !important",
                        }}
                      />
                    ) : (
                      "Generate OTP"
                    )}
                  </Button>
                  <TextField
                    className="w-100"
                    name="otp"
                    disabled={!showOTP}
                    value={phoneSignIn.values.otp}
                    onChange={phoneSignIn.handleChange}
                    onBlur={phoneSignIn.handleBlur}
                    error={
                      phoneSignIn.touched.otp && phoneSignIn.errors.otp
                        ? true
                        : false
                    }
                    helperText={
                      phoneSignIn.touched.otp && phoneSignIn.errors.otp
                    }
                    placeholder="Enter OTP"
                  />
                </span>
              </div>
              <div className="login-by-otp-btn flex-row align-items-center">
                <Button type="submit">
                  {loadingBtn ? (
                    <CircularProgress
                      sx={{
                        color: "white",
                        width: "2rem !important",
                        height: "2rem !important",
                      }}
                    />
                  ) : (
                    "Verify"
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      <form onSubmit={emailSignIn.handleSubmit}>
        <div className="log-in-email auth-input-form">
          <InputLabel> Email </InputLabel>

          <TextField
            className="w-100"
            name="email"
            type={"email"}
            value={emailSignIn.values.email}
            onChange={emailSignIn.handleChange}
            onBlur={emailSignIn.handleBlur}
            error={
              emailSignIn.touched.email && emailSignIn.errors.email
                ? true
                : false
            }
            helperText={emailSignIn.touched.email && emailSignIn.errors.email}
            placeholder="Enter your email"
          />
        </div>

        <div
          aria-label="auth-password"
          className="log-in-password auth-input-form"
        >
          <InputLabel> Password </InputLabel>

          <span className="auth-pass flex-row align-items-center">
            <TextField
              className="w-100"
              placeholder="Enter your password"
              type={visiblePassword ? "text" : "password"}
              name="password"
              value={emailSignIn.values.password}
              onChange={emailSignIn.handleChange}
              onBlur={emailSignIn.handleBlur}
              error={
                emailSignIn.touched.password && emailSignIn.errors.password
                  ? true
                  : false
              }
              helperText={
                emailSignIn.touched.password && emailSignIn.errors.password
              }
            />
            <span
              className="flex-row align-items-center justify-content-center password-eye-icon"
              onClick={() => {
                setVisiblePassword(!visiblePassword);
              }}
            >
              {visiblePassword ? <Visibility /> : <VisibilityOutlined />}
            </span>
          </span>
        </div>
        <div className="auth-btn flex-row gap-2 ">
          <Button type="submit">
            {loadingLogin ? (
              <CircularProgress
                sx={{
                  color: "white",
                  width: "2rem !important",
                  height: "2rem !important",
                }}
              />
            ) : (
              "Login"
            )}
          </Button>

          <Button
            onClick={() => {
              setVerifyByOtp(!verifyByOtp);
            }}
          >
            {" "}
            Login By otp{" "}
          </Button>
        </div>
      </form>
      <div id="re-captcha-verifier2"></div>
    </Auth>
  );
};

export default Login;
